import React, { ReactElement, useContext, useMemo, useState } from 'react';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import { IWorkspaceAction } from '../../@types/workspaceTypes';
import { BasicHeader } from '../BasicHeader';
import ChannelList from '../../graphql/queries/product/ChannelListQuery.graphql';
import BulkChannelUpdate from '../../graphql/mutations/product/ProductBulkChannelUpdateMutation.graphql';
import { ModalBottomButtonContainer } from '../../components/modals/Styles';
import { PrimaryButton, SecondaryButton } from '../../components/connectedForm/Button';
import { useMutation, useQuery } from '@apollo/client';
import {
  IChannelListQueryResult,
  IChannelListQueryVariables,
  IGenericChannel,
  InputMaybe,
  IProductBulkChannelMutationResult,
  IProductBulkChannelMutationVariables,
  Scalars,
} from '../../graphql/schema/p4types';
import { useActionCriteria } from '../../utils/useActionProps';
import getClientMutationId from '../../utils/getClientMutationId';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { IJobMeta, useBackgroundProcess } from '../../utils/BackgroundJobs';
import { cloneDeep } from '@apollo/client/utilities';
import normalizeVariables, { normalizeFields } from '../../graphql/schema/normalizeVariables';
import { Field, FluentProvider, makeStyles, Radio, RadioGroup, webLightTheme } from '@fluentui/react-components';
import { useProductLabelBasicAction } from './useProductLabelBasicAction';
import { useHistory } from 'react-router';
import FluentModal from '../../components/modals/FluentModal';

const useBulkChannelUpdateStyles = makeStyles({
  field: {
    marginBottom: '10px',
  },
});

export enum ProductWorkspaceAction {
  MinMaxLevels = 'PRODUCTS_MINMAX',
  GlobalAmendments = 'PRODUCTS_GLOBAL_AMENDMENTS',
  BulkChannelUpdate = 'BULK_CHANNEL_UPDATE',
}

export const productWorkspaceActionsForPermissions: Array<IWorkspaceAction<ProductWorkspaceAction>> = [
  {
    description: 'Calculate Min/Max Levels',
    actionReference: ProductWorkspaceAction.MinMaxLevels,
  },
  {
    description: 'Run Global Amendments',
    actionReference: ProductWorkspaceAction.GlobalAmendments,
    permissionLabel: 'Global Amendments',
  },
  {
    actionReference: ProductWorkspaceAction.BulkChannelUpdate,
  },
];

enum YesNoUnchanged {
  Yes = 'YES',
  No = 'NO',
  Unchanged = 'UNCHANGED',
}
type IBulkCheckboxUpdateOptions = YesNoUnchanged.Yes | YesNoUnchanged.No | YesNoUnchanged.Unchanged;

const useBulkChannelUpdateAction = (): [IWorkspaceAction<ProductWorkspaceAction>, ReactElement] => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<{
    [key: number]: { channel: IGenericChannel; value: IBulkCheckboxUpdateOptions };
  }>({});
  const workspaceContext = useContext(WorkspaceContext);
  const actionProps = useActionCriteria();
  const styles = useBulkChannelUpdateStyles();

  const { data } = useQuery<IChannelListQueryResult, IChannelListQueryVariables>(ChannelList);

  const handleQueryError = useComponentQueryErrorHandling();
  const [runBulkChannelUpdate] = useMutation<IProductBulkChannelMutationResult, IProductBulkChannelMutationVariables>(
    BulkChannelUpdate,
    {
      onError: handleQueryError,
    }
  );

  const bulkChannelUpdateAction: IWorkspaceAction<ProductWorkspaceAction> = {
    key: 'bulkChannelUpdate',
    actionReference: ProductWorkspaceAction.BulkChannelUpdate,
    text: 'Bulk Channel Update',
    onClick: () => setModalOpen(true),
  };

  const runBackgroundProcess = useBackgroundProcess({
    title: 'Preparing dynamic report',
    onComplete: () => {
      workspaceContext.reloadData();
    },
  });

  const onClickBulkChannelUpdate = () => {
    const channelMap: { [key: number]: IBulkCheckboxUpdateOptions } = {};
    Object.keys(selectedItems).forEach((key) => {
      const selectedChannel = selectedItems[key as unknown as number];
      channelMap[selectedChannel.channel.id] = selectedChannel.value;
    });
    runBulkChannelUpdate({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          actionCriteria: actionProps,
          channels: normalizeVariables(channelMap, [...normalizeFields, 'concurrencyVersion']) as InputMaybe<
            Scalars['Map_Long_YesNoUnchangedScalar']
          >,
        },
      },
    }).then((job) => {
      runBackgroundProcess(cloneDeep(job.data?.productBulkChannelUpdate?.result) as IJobMeta);
      setModalOpen(false);
    });
  };

  const listData = useMemo(
    () => data?.list?.edges?.map((edge) => edge?.node as IGenericChannel) || [],
    [data?.list?.edges]
  );

  const options = listData.map((genericChannel) => (
    <FluentProvider key={genericChannel.uuid!} theme={webLightTheme}>
      <Field label={genericChannel.description!} className={styles.field}>
        <RadioGroup
          layout={'horizontal'}
          defaultValue={YesNoUnchanged.Unchanged}
          onChange={(_, data) => {
            setSelectedItems((selectedItems) => {
              return {
                ...selectedItems,
                [genericChannel.id]: { channel: genericChannel, value: data.value as IBulkCheckboxUpdateOptions },
              };
            });
          }}
        >
          <Radio value={YesNoUnchanged.Yes} label={'Yes'} />
          <Radio value={YesNoUnchanged.No} label={'No'} />
          <Radio value={YesNoUnchanged.Unchanged} label={'Unchanged'} />
        </RadioGroup>
      </Field>
    </FluentProvider>
  ));

  const modals = (
    <FluentModal isOpen={modalOpen} styles={{ main: { paddingBottom: '60px' } }}>
      <BasicHeader>Bulk Channel Update ({workspaceContext.selectionState.count ?? 0} Records)</BasicHeader>
      <div style={{ padding: '1em' }}>{options}</div>
      <ModalBottomButtonContainer>
        <SecondaryButton onClick={() => setModalOpen(false)}>Cancel</SecondaryButton>
        <PrimaryButton onClick={onClickBulkChannelUpdate}>Update</PrimaryButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );

  return [bulkChannelUpdateAction, modals];
};

export const useProductWorkspaceActions: () => [Array<IWorkspaceAction<ProductWorkspaceAction>>, ReactElement] = () => {
  const [productLabelAction, productLabelModals] = useProductLabelBasicAction();
  const history = useHistory();
  const workspace = useContext(WorkspaceContext);
  const [bulkChannelUpdateAction, bulkChannelUpdateModal] = useBulkChannelUpdateAction();

  return [
    [
      {
        key: 'calculateMinMaxLevels',
        actionReference: ProductWorkspaceAction.MinMaxLevels,
        text: 'Calculate Min/Max Levels',
        ignoreBulkActionCheck: true,
        onClick: () => {
          history.push({
            pathname: `/Products/MinMaxLevels/Parameters`,
            state: { ...workspace.selectionState, previousLocation: history.location?.pathname || 'back' },
          });
        },
      },
      {
        key: 'productGlobalAmendments',
        actionReference: ProductWorkspaceAction.GlobalAmendments,
        text: 'Run Global Amendments',
        onClick: () => {
          history.push({
            pathname: '/Products/GlobalAmendments/New',
            state: { ...workspace.selectionState, previousLocation: history.location?.pathname || 'back' },
          });
        },
      },
      productLabelAction as IWorkspaceAction<ProductWorkspaceAction>, //Narrowing type as the same hook is also used for the form action.
      bulkChannelUpdateAction,
    ],
    <>
      {productLabelModals}
      {bulkChannelUpdateModal}
    </>,
  ];
};
